import { useState, useEffect, useMemo } from "react";
import { Sv, ic } from "../../components/common/Icons";
import ITAModal from "./ITAModal";
import api from "../../services/api";import "../../assets/styles/ITAdmin/ITACredsPg.css";

// ── CSV export for credentials ──
const monthKey = (d) => {
  if (!d) return null;
  try { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`; } catch { return null; }
};
const monthLabel = (key) => {
  if (!key) return "";
  const [y,m] = key.split("-");
  return new Date(Number(y), Number(m)-1, 1).toLocaleDateString("en-IN", { month:"long", year:"numeric" });
};

function exportCredCSV(rows, filename) {
  const headers = ["Employee ID","Employee Name","Department","Laptop Username","IP Address","VPN","Email","Software","AI Tools","System Access","Added On"];
  const lines = [headers.join(",")];
  rows.forEach(c => {
    const sw = Array.isArray(c.software) ? c.software.join("; ") : (c.software||"");
    const ai = Array.isArray(c.ai) ? c.ai.join("; ") : (c.ai||"");
    const sa = Array.isArray(c.sysAccess) ? c.sysAccess.join("; ") : (c.sysAccess||"");
    const addedOn = c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN") : "";
    lines.push([
      c.empId, c.empName, c.department||"", c.lapUser, c.ip||"",
      c.vpn ? "Yes" : "No", c.email||"", sw, ai, sa, addedOn
    ].map(v => `"${String(v||"").replace(/"/g,'""')}"`).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type:"text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

function ITACredsPg() {
  const [cList, setCList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [suc, setSuc] = useState("");
  const [showPw, setShowPw] = useState({});
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");
  const [showMonthly, setShowMonthly] = useState(false);
  const [activeTab, setActiveTab] = useState("list"); // list | bulk
  const [showDemoC, setShowDemoC] = useState(false);
  const [bulkFileC, setBulkFileC] = useState(null);
  const [bulkDragC, setBulkDragC] = useState(false);
  const [bulkUploadingC, setBulkUploadingC] = useState(false);
  const [bulkResultC, setBulkResultC] = useState(null);

  // Form state for add/edit
  const emptyForm = { empId: "", lapUser: "", lapPass: "", ip: "", software: "", ai: "", vpn: false, email: "", sysAccess: "" };
  const [form, setForm] = useState({ ...emptyForm });

  // Employee list for dropdown
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchCreds();
    fetchEmployees();
  }, []);

  const fetchCreds = async () => {
    try {
      const data = await api.getITCredentials();
      setCList((data || []).map((c) => ({
        id: c.id,
        empId: c.user_id ? String(c.user_id) : "",
        empName: c.emp_name || "",
        lapUser: c.laptop_user || "",
        lapPass: c.laptop_pass || "",
        ip: c.ip_address || "",
        vpn: !!c.vpn_enabled,
        email: c.email || "",
        department: c.department || "",
        created_at: c.created_at || c.updated_at || null,
        software: typeof c.software === "string" ? JSON.parse(c.software || "[]") : c.software || [],
        ai: typeof c.ai_tools === "string" ? JSON.parse(c.ai_tools || "[]") : c.ai_tools || [],
        sysAccess: typeof c.system_access === "string" ? JSON.parse(c.system_access || "[]") : c.system_access || []
      })));
    } catch (e) {console.warn("Failed to load credentials:", e.message);} finally
    {setLoading(false);}
  };

  const fetchEmployees = async () => {
    try {
      const data = await api.getITPeople();
      setEmployees(data || []);
    } catch (e) {console.warn(e);}
  };

  const showSuccess = (msg) => {setSuc(msg);setTimeout(() => setSuc(""), 4000);};

  // ── Derived data ──
  const availMonths = useMemo(() => {
    const set = new Set();
    cList.forEach(c => { const k = monthKey(c.created_at); if (k) set.add(k); });
    return ["all", ...Array.from(set).sort((a,b) => b.localeCompare(a))];
  }, [cList]);

  const filtered = useMemo(() => {
    let list = cList;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.empName||"").toLowerCase().includes(q) ||
        (c.lapUser||"").toLowerCase().includes(q) ||
        (c.email||"").toLowerCase().includes(q) ||
        (c.ip||"").toLowerCase().includes(q) ||
        (c.department||"").toLowerCase().includes(q)
      );
    }
    if (monthFilter !== "all") {
      list = list.filter(c => monthKey(c.created_at) === monthFilter);
    }
    return list;
  }, [cList, search, monthFilter]);

  // ── ADD ──
  const openAdd = () => {
    setForm({ ...emptyForm });
    setShowAdd(true);
  };

  const handleAdd = async () => {
    if (!form.empId || !form.lapUser || !form.lapPass) {
      alert("Employee, Username, and Password are required");return;
    }
    try {
      await api.addITCredential({
        empId: form.empId,
        laptopUser: form.lapUser,
        laptopPass: form.lapPass,
        ipAddress: form.ip,
        vpnEnabled: form.vpn,
        email: form.email,
        software: form.software.split(",").map((s) => s.trim()).filter(Boolean),
        aiTools: form.ai.split(",").map((s) => s.trim()).filter(Boolean),
        systemAccess: form.sysAccess.split(",").map((s) => s.trim()).filter(Boolean)
      });
      showSuccess("Credential added!");
      setShowAdd(false);
      await fetchCreds();
    } catch (e) {alert("Error: " + e.message);}
  };

  // ── EDIT ──
  const openEdit = (c) => {
    setForm({
      empId: c.empId,
      lapUser: c.lapUser,
      lapPass: c.lapPass,
      ip: c.ip,
      software: (Array.isArray(c.software) ? c.software : []).join(", "),
      ai: (Array.isArray(c.ai) ? c.ai : []).join(", "),
      vpn: c.vpn,
      email: c.email,
      sysAccess: (Array.isArray(c.sysAccess) ? c.sysAccess : []).join(", ")
    });
    setShowEdit(c);
  };

  const handleEdit = async () => {
    if (!showEdit) return;
    try {
      await api.updateITCredential({
        userId: showEdit.empId,
        laptopUser: form.lapUser,
        laptopPass: form.lapPass,
        ipAddress: form.ip,
        vpnEnabled: form.vpn,
        email: form.email,
        software: form.software.split(",").map((s) => s.trim()).filter(Boolean),
        aiTools: form.ai.split(",").map((s) => s.trim()).filter(Boolean),
        systemAccess: form.sysAccess.split(",").map((s) => s.trim()).filter(Boolean)
      });
      showSuccess("Credentials updated!");
      setShowEdit(null);
      await fetchCreds();
    } catch (e) {alert("Error: " + e.message);}
  };

  // ── DELETE ──
  const handleDelete = async (c) => {
    if (!window.confirm(`Delete credentials for ${c.empName}?`)) return;
    try {
      await api.deleteITCredential(c.empId);
      showSuccess("Credential deleted!");
      await fetchCreds();
    } catch (e) {alert("Error: " + e.message);}
  };

  // ── FORM FIELDS (shared between add & edit) ──
  const renderForm = (isEdit) =>
  <>
      {!isEdit &&
    <div className="form-group">
          <label className="form-label">Employee *</label>
          <select className="form-input" value={form.empId}
            onChange={(e) => {
              const selectedId = e.target.value;
              const emp = employees.find(em => String(em.id) === String(selectedId));
              setForm({
                ...form,
                empId: selectedId,
                email: emp?.email || form.email || ""
              });
            }} required>
            <option value="">Select Employee</option>
            {employees.map((emp) =>
        <option key={emp.id} value={emp.id}>{emp.name} ({emp.id})</option>
        )}
          </select>
        </div>
    }
      <div className="itacredspg-inline-1">
        <div className="form-group">
          <label className="form-label">Laptop Username *</label>
          <input className="form-input" value={form.lapUser} onChange={(e) => setForm({ ...form, lapUser: e.target.value })} placeholder="e.g. karthik.r" />
        </div>
        <div className="form-group">
          <label className="form-label">Laptop Password *</label>
          <input className="form-input" value={form.lapPass} onChange={(e) => setForm({ ...form, lapPass: e.target.value })} placeholder="e.g. Temp@1234" />
        </div>
      </div>
      <div className="itacredspg-inline-2">
        <div className="form-group">
          <label className="form-label">IP Address</label>
          <input className="form-input" value={form.ip} onChange={(e) => setForm({ ...form, ip: e.target.value })} placeholder="e.g. 192.168.1.50" />
        </div>
        <div className="form-group">
          <label className="form-label">VPN Access</label>
          <select className="form-input" value={form.vpn ? "yes" : "no"} onChange={(e) => setForm({ ...form, vpn: e.target.value === "yes" })}>
            <option value="no">Disabled</option>
            <option value="yes">Enabled</option>
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Email</label>
        <input className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="e.g. karthik@company.com" />
      </div>
      <div className="form-group">
        <label className="form-label">Software / Tools (comma separated)</label>
        <input className="form-input" value={form.software} onChange={(e) => setForm({ ...form, software: e.target.value })} placeholder="e.g. VS Code, Figma, Slack" />
      </div>
      <div className="form-group">
        <label className="form-label">AI Tools (comma separated)</label>
        <input className="form-input" value={form.ai} onChange={(e) => setForm({ ...form, ai: e.target.value })} placeholder="e.g. GitHub Copilot, ChatGPT Team" />
      </div>
      <div className="form-group">
        <label className="form-label">System Access (comma separated)</label>
        <input className="form-input" value={form.sysAccess} onChange={(e) => setForm({ ...form, sysAccess: e.target.value })} placeholder="e.g. Jira, AWS Console, GitLab" />
      </div>
    </>;


  return (
    <div>
      <div className="itacredspg-inline-3">Credentials & Access Management</div>

      {suc &&
      <div className="itacredspg-inline-4">
          <Sv d={ic.chk} s={16} c="#2563eb" /> {suc}
        </div>
      }

      {/* ── Tab Switcher ── */}
      <div style={{ display:"flex", gap:8, marginBottom:16 }}>
        <button onClick={() => setActiveTab("list")}
          style={{ padding:"7px 18px", background: activeTab==="list" ? "#0f172a" : "#fff",
            color: activeTab==="list" ? "#fff" : "#374151",
            border:"1.5px solid " + (activeTab==="list" ? "#0f172a" : "#d1d5db"),
            borderRadius:6, fontWeight:700, fontSize:12, cursor:"pointer" }}>
          Credentials List
        </button>
        <button onClick={() => setActiveTab("bulk")}
          style={{ padding:"7px 18px", background: activeTab==="bulk" ? "#0f172a" : "#fff",
            color: activeTab==="bulk" ? "#fff" : "#374151",
            border:"1.5px solid " + (activeTab==="bulk" ? "#0f172a" : "#d1d5db"),
            borderRadius:6, fontWeight:700, fontSize:12, cursor:"pointer" }}>
          ⬆ Bulk Upload
        </button>
      </div>

      {/* ══════ BULK UPLOAD TAB ══════ */}
      {activeTab === "bulk" && (
        <div style={{ background:"#fff", border:"1px solid #e8eaed", borderRadius:8, overflow:"hidden" }}>

          {/* Top bar */}
          <div style={{ padding:"18px 24px", borderBottom:"1px solid #e8eaed", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div>
              <div style={{ fontWeight:700, fontSize:15, color:"#1a1a2e" }}>Bulk Credential Upload</div>
              <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>Upload a CSV to assign credentials to multiple employees at once</div>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => setShowDemoC(v => !v)}
                style={{ padding:"7px 16px", background:"#fff", border:"1px solid #d1d5db", borderRadius:6, fontWeight:600, fontSize:12, color:"#374151", cursor:"pointer" }}>
                {showDemoC ? "Hide Demo File" : "Show Demo File"}
              </button>
              <button onClick={() => {
                const headers = ["emp_id","laptop_user","laptop_pass","ip_address","email","vpn","software","ai_tools","system_access"];
                const rows = [
                  "EMP-2024-001,john.doe,Pass@1234,192.168.1.10,john@company.com,yes,MS Office;Slack,GitHub Copilot,CRM;ERP",
                  "EMP-2024-002,jane.smith,Pass@5678,192.168.1.11,jane@company.com,no,MS Office;Figma,,CRM",
                ];
                const csv = [headers.join(","), ...rows].join("\n");
                const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
                a.download = "demo_credentials.csv"; a.click(); URL.revokeObjectURL(a.href);
              }}
                style={{ padding:"7px 16px", background:"#fff", border:"1px solid #d1d5db", borderRadius:6, fontWeight:600, fontSize:12, color:"#374151", cursor:"pointer" }}>
                Download Sample CSV
              </button>
            </div>
          </div>

          {/* Demo table */}
          {showDemoC && (
            <div style={{ borderBottom:"1px solid #e8eaed", overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, minWidth:860 }}>
                <thead>
                  <tr style={{ background:"#f9fafb" }}>
                    {["EMP_ID","LAPTOP_USER","LAPTOP_PASS","IP_ADDRESS","EMAIL","VPN","SOFTWARE","AI_TOOLS","SYSTEM_ACCESS"].map(h => (
                      <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11,
                        color:"#6b7280", letterSpacing:".4px", borderBottom:"1px solid #e8eaed", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["EMP-2024-001","john.doe","Pass@1234","192.168.1.10","john@company.com","yes","MS Office; Slack","GitHub Copilot","CRM; ERP"],
                    ["EMP-2024-002","jane.smith","Pass@5678","192.168.1.11","jane@company.com","no","MS Office; Figma","—","CRM"],
                  ].map((row, ri) => (
                    <tr key={ri} style={{ background: ri%2===0?"#fff":"#fafafa" }}>
                      {row.map((v,i) => (
                        <td key={i} style={{ padding:"9px 14px", color: v==="—"?"#d1d5db":"#374151", fontSize:12 }}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding:"8px 14px", background:"#f9fafb", fontSize:11, color:"#9ca3af" }}>
                * <strong>Required:</strong> emp_id &nbsp;|&nbsp; Software / AI Tools / System Access: separate multiple values with semicolon (;) &nbsp;|&nbsp; VPN: yes/no
              </div>
            </div>
          )}

          {/* Upload area */}
          <div style={{ padding:"24px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16,
              padding:"10px 14px", border:"1px solid #e8eaed", borderRadius:6, background:"#fafafa" }}>
              <label style={{ cursor:"pointer" }}>
                <span style={{ padding:"6px 12px", border:"1px solid #d1d5db", borderRadius:4,
                  background:"#fff", fontSize:12, fontWeight:500, color:"#374151",
                  boxShadow:"0 1px 2px rgba(0,0,0,.05)" }}>Choose File</span>
                <input type="file" accept=".csv" style={{ display:"none" }}
                  onChange={e => { const f = e.target.files[0]; if(f){setBulkFileC(f);setBulkResultC(null);e.target.value="";} }} />
              </label>
              <span style={{ fontSize:12, color: bulkFileC ? "#374151" : "#9ca3af" }}>
                {bulkFileC ? bulkFileC.name : "No file chosen"}
              </span>
              {bulkFileC && (
                <>
                  <span style={{ fontSize:11, color:"#9ca3af" }}>({(bulkFileC.size/1024).toFixed(1)} KB)</span>
                  <button onClick={() => { setBulkFileC(null); setBulkResultC(null); }}
                    style={{ marginLeft:"auto", padding:"3px 10px", border:"1px solid #fecaca",
                      borderRadius:4, background:"#fef2f2", color:"#ef4444", fontWeight:600, fontSize:11, cursor:"pointer" }}>✕ Remove</button>
                </>
              )}
            </div>

            <div onDragOver={e=>{e.preventDefault();setBulkDragC(true);}} onDragLeave={()=>setBulkDragC(false)}
              onDrop={e=>{e.preventDefault();setBulkDragC(false);const f=e.dataTransfer.files[0];if(f&&f.name.endsWith(".csv")){setBulkFileC(f);setBulkResultC(null);}}}
              style={{ border:`2px dashed ${bulkDragC?"#2563eb":"#e5e7eb"}`, borderRadius:6, padding:"16px",
                textAlign:"center", marginBottom:20, background: bulkDragC?"#eff6ff":"#fafafa",
                transition:"all .15s", fontSize:12, color:"#9ca3af" }}>
              {bulkDragC ? "Drop your CSV file here" : "or drag and drop your CSV file here"}
            </div>

            <button disabled={!bulkFileC || bulkUploadingC}
              onClick={async () => {
                if (!bulkFileC) return;
                setBulkUploadingC(true); setBulkResultC(null);
                try {
                  const result = await api.bulkUploadCredentials(bulkFileC);
                  setBulkResultC(result);
                  if (result.insertedCount > 0) {
                    showSuccess(`${result.insertedCount} credentials uploaded successfully`);
                    await fetchCreds();
                  }
                } catch(e) { setBulkResultC({ error: e.message }); }
                setBulkUploadingC(false);
              }}
              style={{ padding:"10px 28px", background: !bulkFileC ? "#e5e7eb" : "#1d4ed8",
                color: !bulkFileC ? "#9ca3af" : "#fff", border:"none", borderRadius:6,
                fontWeight:700, fontSize:13, cursor: bulkFileC ? "pointer" : "not-allowed" }}>
              {bulkUploadingC ? "Uploading..." : "Upload Credentials CSV"}
            </button>
          </div>

          {/* Results */}
          {bulkResultC && !bulkResultC.error && (
            <div style={{ borderTop:"1px solid #e8eaed" }}>
              <div style={{ display:"flex", gap:0, borderBottom:"1px solid #e8eaed" }}>
                {[
                  ["Inserted/Updated", bulkResultC.insertedCount, "#16a34a", "#f0fdf4"],
                  ["Skipped",          bulkResultC.skipped,       "#d97706", "#fffbeb"],
                  ["Warnings",         bulkResultC.errors?.length||0, "#dc2626", "#fef2f2"],
                ].map(([label, val, color, bg]) => (
                  <div key={label} style={{ flex:1, padding:"16px 24px", background:bg,
                    borderRight:"1px solid #e8eaed", textAlign:"center" }}>
                    <div style={{ fontSize:26, fontWeight:800, color }}>{val}</div>
                    <div style={{ fontSize:11, color:"#6b7280", fontWeight:600, marginTop:2 }}>{label}</div>
                  </div>
                ))}
                <div style={{ display:"flex", alignItems:"center", padding:"0 24px" }}>
                  <button onClick={() => { setActiveTab("list"); setBulkResultC(null); setBulkFileC(null); }}
                    style={{ padding:"8px 18px", background:"#1d4ed8", color:"#fff", border:"none",
                      borderRadius:6, fontWeight:700, fontSize:12, cursor:"pointer" }}>
                    View Credentials →
                  </button>
                </div>
              </div>

              {Array.isArray(bulkResultC.inserted) && bulkResultC.inserted.length > 0 && (
                <div style={{ overflowX:"auto" }}>
                  <div style={{ padding:"12px 24px 8px", fontSize:12, fontWeight:700, color:"#374151",
                    borderBottom:"1px solid #f3f4f6" }}>Successfully Processed</div>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                    <thead>
                      <tr style={{ background:"#f9fafb" }}>
                        {["EMP ID","NAME","LAPTOP USER","IP ADDRESS","EMAIL","VPN"].map(h=>(
                          <th key={h} style={{ padding:"9px 16px", textAlign:"left", fontWeight:700,
                            fontSize:11, color:"#6b7280", letterSpacing:".4px", borderBottom:"1px solid #e8eaed" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResultC.inserted.map((r,i)=>(
                        <tr key={i} style={{ borderBottom:"1px solid #f3f4f6", background:i%2===0?"#fff":"#fafafa" }}>
                          <td style={{ padding:"9px 16px", fontFamily:"monospace", color:"#1d4ed8", fontWeight:700 }}>{r.empId}</td>
                          <td style={{ padding:"9px 16px", fontWeight:600, color:"#111827" }}>{r.empName}</td>
                          <td style={{ padding:"9px 16px", color:"#374151" }}>{r.laptopUser||"—"}</td>
                          <td style={{ padding:"9px 16px", color:"#374151", fontFamily:"monospace" }}>{r.ipAddress||"—"}</td>
                          <td style={{ padding:"9px 16px", color:"#374151" }}>{r.email||"—"}</td>
                          <td style={{ padding:"9px 16px" }}>
                            <span style={{ padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:600,
                              background: r.vpn?"#d1fae5":"#f3f4f6", color: r.vpn?"#065f46":"#6b7280" }}>
                              {r.vpn ? "Yes" : "No"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {bulkResultC.errors?.length > 0 && (
                <div style={{ padding:"12px 24px", background:"#fffbeb", borderTop:"1px solid #fef9c3" }}>
                  <div style={{ fontWeight:700, fontSize:12, color:"#92400e", marginBottom:6 }}>Warnings</div>
                  {bulkResultC.errors.map((e,i)=>(
                    <div key={i} style={{ fontSize:11, color:"#b45309", padding:"2px 0" }}>• {e}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {bulkResultC?.error && (
            <div style={{ margin:"0 24px 24px", padding:"12px 16px", background:"#fef2f2",
              border:"1px solid #fecaca", borderRadius:6, color:"#dc2626", fontSize:13, fontWeight:600 }}>
              Upload failed: {bulkResultC.error}
            </div>
          )}
        </div>
      )}

      {/* ══════ LIST TAB ══════ */}
      {activeTab === "list" && <>

      {/* ── Toolbar: Search + Download ── */}
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:14, alignItems:"center" }}>
        {/* Search */}
        <input placeholder="🔍 Search name, email, IP…" value={search} onChange={e=>setSearch(e.target.value)}
          style={{ padding:"8px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12,
            flex:1, minWidth:180, outline:"none" }} />

        {/* Month filter */}
        <select value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}
          style={{ padding:"8px 12px", border:"1px solid #e2e8f0", borderRadius:8,
            fontSize:12, color:"#374151", background:"#fff", cursor:"pointer" }}>
          <option value="all">All Months</option>
          {availMonths.filter(m=>m!=="all").map(m=>(
            <option key={m} value={m}>{monthLabel(m)}</option>
          ))}
        </select>

        {/* Monthly dropdown download */}
        <div style={{ position:"relative" }}>
          <button onClick={() => setShowMonthly(v=>!v)}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"8px 14px",
              background:"#fff", border:"1px solid #e2e8f0", borderRadius:8,
              fontWeight:600, fontSize:12, color:"#374151", cursor:"pointer" }}>
            📅 Monthly CSV ▾
          </button>
          {showMonthly && (
            <div style={{ position:"absolute", right:0, top:"calc(100% + 6px)", background:"#fff",
              border:"1px solid #e2e8f0", borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,.12)",
              zIndex:100, minWidth:230, overflow:"hidden" }}>
              <div style={{ padding:"8px 12px", fontSize:11, fontWeight:700, color:"#64748b",
                borderBottom:"1px solid #f1f5f9", background:"#f8fafc" }}>
                SELECT MONTH (AUDIT)
              </div>
              {availMonths.filter(m=>m!=="all").length === 0 ? (
                <div style={{ padding:"12px", fontSize:12, color:"#94a3b8" }}>No records yet</div>
              ) : availMonths.filter(m=>m!=="all").map(m => {
                const mRows = cList.filter(c => monthKey(c.created_at) === m);
                return (
                  <button key={m}
                    onClick={() => { exportCredCSV(mRows, `credentials_${m}.csv`); setShowMonthly(false); }}
                    style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                      width:"100%", padding:"10px 14px", border:"none", background:"#fff",
                      cursor:"pointer", fontSize:13, borderBottom:"1px solid #f8fafc" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                    onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                    <span style={{ fontWeight:600, color:"#1e293b" }}>{monthLabel(m)}</span>
                    <span style={{ fontSize:11, color:"#94a3b8" }}>{mRows.length} records</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Download filtered CSV */}
        <button onClick={() => exportCredCSV(filtered, `credentials${monthFilter!=="all"?"_"+monthFilter:""}.csv`)}
          style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px",
            background:"#0f172a", color:"#fff", border:"none", borderRadius:8,
            fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap" }}>
          ⬇ Download CSV {filtered.length !== cList.length ? `(${filtered.length})` : ""}
        </button>
      </div>

      {/* Monthly audit banner */}
      {monthFilter !== "all" && (
        <div style={{ background:"linear-gradient(135deg,#1e1b4b,#4f46e5)", borderRadius:10,
          padding:"12px 18px", marginBottom:14, display:"flex", justifyContent:"space-between",
          alignItems:"center", color:"#fff" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:14 }}>📅 Audit: {monthLabel(monthFilter)}</div>
            <div style={{ fontSize:11, opacity:.8, marginTop:2 }}>{filtered.length} credentials added this month</div>
          </div>
          <button onClick={() => exportCredCSV(filtered, `credentials_audit_${monthFilter}.csv`)}
            style={{ padding:"7px 14px", background:"rgba(255,255,255,.2)", border:"1px solid rgba(255,255,255,.3)",
              borderRadius:8, color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>
            ⬇ Download Audit CSV
          </button>
        </div>
      )}

      <div className="card animate-in">
        <div className="card-header">
          <div>
            <div className="card-title">Employee Credentials</div>
            <div className="card-subtitle">{filtered.length} of {cList.length} records</div>
          </div>
          <button className="btn-outline itacredspg-inline-5" onClick={openAdd}>
            <Sv d={ic.plus} s={14} /> Add Credential
          </button>
        </div>
        <div className="card-body itacredspg-inline-6">
          {loading ?
          <div className="itacredspg-inline-7">Loading...</div> :
          filtered.length === 0 ?
          <div className="itacredspg-inline-8">{cList.length === 0 ? 'No credentials yet. Click "Add Credential" to create one.' : "No results match your filter."}</div> :

          <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th><th>Dept</th><th>Username</th><th>Password</th><th>IP Address</th>
                  <th>VPN</th><th>Email</th><th>Software</th><th>AI Tools</th><th>Added On</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) =>
              <tr key={c.empId}>
                    <td>
                      <div className="itacredspg-inline-9">{c.empName}</div>
                      <div className="itacredspg-inline-10">{c.empId}</div>
                    </td>
                    <td style={{ fontSize:11, color:"#64748b" }}>{c.department||"—"}</td>
                    <td className="itacredspg-inline-11">{c.lapUser}</td>
                    <td className="itacredspg-inline-12">
                      {showPw[c.empId] ? c.lapPass : "••••••••"}{" "}
                      <span onClick={() => setShowPw((p) => ({ ...p, [c.empId]: !p[c.empId] }))} className="itacredspg-inline-13">
                        <Sv d={showPw[c.empId] ? ic.x : ic.eye} s={10} c="#6366f1" />
                      </span>
                    </td>
                    <td className="itacredspg-inline-14">{c.ip || "—"}</td>
                    <td>
                      <span style={{ "--itacredspg-1-background":

                    c.vpn ? "#eff6ff" : "#fef2f2", "--itacredspg-1-color":
                    c.vpn ? "#2563eb" : "#ef4444" }} className="itacredspg-dyn-1">
                    
                        {c.vpn ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="itacredspg-inline-15">{c.email || "—"}</td>
                    <td>
                      <div className="itacredspg-inline-16">
                        {(Array.isArray(c.software) ? c.software : []).slice(0, 3).map((s, i) =>
                    <span key={i} className="itacredspg-inline-17">{s}</span>
                    )}
                        {(Array.isArray(c.software) ? c.software : []).length > 3 &&
                    <span className="itacredspg-inline-18">+{c.software.length - 3}</span>
                    }
                        {(Array.isArray(c.software) ? c.software : []).length === 0 && <span className="itacredspg-inline-19">—</span>}
                      </div>
                    </td>
                    <td>
                      <div className="itacredspg-inline-20">
                        {(Array.isArray(c.ai) ? c.ai : []).length === 0 ?
                    <span className="itacredspg-inline-21">—</span> :
                    (Array.isArray(c.ai) ? c.ai : []).map((a, i) =>
                    <span key={i} className="itacredspg-inline-22">{a}</span>
                    )}
                      </div>
                    </td>
                    <td style={{ fontSize:11, color:"#94a3b8", whiteSpace:"nowrap" }}>
                      {c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "—"}
                    </td>
                    <td>
                      <div className="itacredspg-inline-23">
                        <button onClick={() => openEdit(c)} className="itacredspg-inline-24">
                          ✏️ Edit
                        </button>
                        <button onClick={() => handleDelete(c)} className="itacredspg-inline-25">
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
              )}
              </tbody>
            </table>
          }
        </div>
      </div>

      {/* ADD MODAL */}
      {showAdd &&
      <ITAModal open={true} onClose={() => setShowAdd(false)} title="Add New Credential">
          {renderForm(false)}
          <div className="form-actions itacredspg-inline-26">
            <button className="btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
            <button className="btn-submit" onClick={handleAdd}>Add Credential</button>
          </div>
        </ITAModal>
      }

      {/* EDIT MODAL */}
      {showEdit &&
      <ITAModal open={true} onClose={() => setShowEdit(null)} title={"Edit Credentials — " + (showEdit.empName || "")}>
          {renderForm(true)}
          <div className="form-actions itacredspg-inline-27">
            <button className="btn-outline" onClick={() => setShowEdit(null)}>Cancel</button>
            <button className="btn-submit" onClick={handleEdit}>Save Changes</button>
          </div>
        </ITAModal>
      }
      </> /* end activeTab==="list" */}
    </div>);

}

export default ITACredsPg;